import { createTypedOutlet } from "src/router";
import type { DerivedCampaignStatus, GetCampaignByIdQuery } from "src/graphql";
import type { OutletMessage } from "src/cep/messages/message/outlet";
import type { ChannelType } from "src/types/cep";
import type { VisualQueryFilter } from "src/types/visual";
import type { CampaignChecklistItem } from "./completeness";
import type { CampaignType, TimezoneMode } from "./form-types";

type RawCampaign = NonNullable<GetCampaignByIdQuery["campaigns_by_pk"]>;
type RawSegment = NonNullable<RawCampaign["segment"]>;

type Segment = Omit<RawSegment, "visual_query_filter"> & {
  visual_query_filter: VisualQueryFilter | null;
};

export type Campaign = Omit<
  RawCampaign,
  "segment" | "timezone_mode" | "campaign_type"
> & {
  segment: Segment | null;
  /** Narrowed at the outlet boundary from the campaign's timezone_mode column. */
  timezoneMode: TimezoneMode;
  /** Narrowed at the outlet boundary from the campaign's campaign_type column. */
  campaign_type: CampaignType | null;
};

/**
 * Mirrors CampaignRunStatus from @hightouch/lib/cep/types
 * (not exposed via GraphQL — campaign_runs.status is a raw string from Hasura)
 */
export const CampaignRunStatus = {
  InProgress: "in_progress",
  Scheduling: "scheduling",
  Sending: "sending",
  Completed: "completed",
  Canceled: "cancelled",
  Error: "error",
} as const;

export type CepReachability = {
  channelType: string;
  hasSubscriptionTopic: boolean;
  /** Transactional messages have no topic by design — reachable = contactable. */
  transactional?: boolean | null;
};

export type CampaignOutletContext = {
  campaign: Campaign;
  // TODO(omnichannel): When campaigns support multiple channels, each drawer
  // should select the appropriate message instead of defaulting to messages[0].
  messages: OutletMessage[];
  channelTypes: ChannelType[];
  parentModelId: string | undefined;
  derivedStatus: DerivedCampaignStatus | undefined;
  // TODO: support multi-message campaigns — reachability should cover all messages
  cepReachability: CepReachability | undefined;
  /** What Activate still needs, per section; each overview card's title lists its own. */
  checklist: CampaignChecklistItem[];
  /** True when the campaign is viewed nested inside a journey builder. Controls
   *  minimal-header mode. Defaults to `false` until journey-nested detection lands. */
  isJourneyNested: boolean;
};

const {
  OutletProvider: CampaignOutlet,
  ContextProvider: CampaignOutletContextProvider,
  useContext: useCampaignOutletContext,
  useOptionalContext: useCampaignOutletContextIfAvailable,
} = createTypedOutlet<CampaignOutletContext>("Campaign");

export {
  CampaignOutlet,
  CampaignOutletContextProvider,
  useCampaignOutletContext,
  useCampaignOutletContextIfAvailable,
};
